import MonthPerPageA4 from "./MonthPerPageA4";
import SixMonthsPerPageA4 from "./SixMonthsPerPageA4";
import WeekPerPageA4 from "./WeekPerPageA4";
import WeekPerPageA5 from "./WeekPerPageA5";
import YearMonthMultiPageA4 from "./YearMonthMultiPageA4";
import YearPerPageA4 from "./YearPerPageA4";
import YearPerPageA5 from "./YearPerPageA5";
import YearPerPageUSLetter from "./YearPerPageUSLetter";

// source: https://www.abeautifulsite.net/posts/getting-localized-month-and-day-names-in-the-browser/
function getDayNames(locale = 'en', format = 'long') {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: format, timeZone: 'UTC' });
    const days = [1, 2, 3, 4, 5, 6, 7].map(day => {
        const dd = day < 10 ? `0${day}` : day;
        return new Date(`2017-01-${dd}T00:00:00+00:00`);
    });
    return days.map(date => formatter.format(date));
}

/**
 * Backward-compatible page title template parser.
 * Delegates to the generic `titleTemplateParser` by constructing
 * the appropriate context object.
 *
 * @param {string} template
 *        Template string containing placeholders like `$YEAR$`.
 * @param {string | number | Array<string | number>} year
 * @param {string | number} monthNumber
 * @param {string} monthName
 * @param {string | number} week
 * @returns {string}
 *        Parsed page title.
 */
const pageTitleTemplateParser = (
    template,
    year,
    monthNumber,
    monthName,
    week
) => {
    const context = {
        year,
        month: monthNumber,
        month_name: monthName,
        week,
    };

    return titleTemplateParser(context, template);
};

/**
 * Parses a template string by replacing placeholders of the form `$KEY$`
 * using values from a context object.
 *
 * Supported placeholder syntax:
 *
 *   $KEY$
 *   $KEY|ARRAY_DELIM: ,$
 *   $KEY|ARRAY_DELIM: |$
 *   $KEY|DEFAULT: Unknown$
 *
 * Combined / flexible syntax (order-independent):
 *
 *   $KEY|ARRAY_DELIM: ,DEFAULT: N/A$
 *   $KEY|ARRAY_DELIM: D E L DEFAULT: N/A$
 *   $KEY|ARRAY_DELIM:,DEFAULT:N/A$
 *   $KEY| DEFAULT: N/A ARRAY_DELIM: ,$
 *
 * Rules:
 * - KEY is matched case-insensitively
 * - KEY is converted to camelCase using `_` as word delimiter
 *     - Examples:
 *         TEST_KEY -> testKey
 *         KEY      -> key
 * - Arguments appear after `|`
 * - Whitespace after `|` is allowed
 * - Argument order is irrelevant
 * - ARRAY_DELIM:
 *     - Defines how array values are joined
 *     - May be any string (including spaces or symbols)
 * - DEFAULT:
 *     - Used when the resolved value is null, undefined, or empty string
 * - Argument values end:
 *     - before another argument keyword (`ARRAY_DELIM:` / `DEFAULT:`), or
 *     - at the closing `$`
 * - Function values:
 *     - If the resolved context value is a function, it is invoked with no
 *       arguments and its return value is used
 *     - The function must return a string (or a value convertible to string)
 *
 * @param {Record<
 *   string,
 *   string
 *   | number
 *   | Array<string | number>
 *   | (() => string)
 *   | null
 *   | undefined
 * >} context
 *        Object defining available template variables.
 * @param {string} template
 *        Template string containing placeholders like `$YEAR$`.
 * @returns {string}
 *        Parsed template string.
 */
const titleTemplateParser = (context, template) => {
    if (!template) {
        return template;
    }

    const ctx = context ?? {};

    const toCamelCase = (key) => {
        return key
            .toLowerCase()
            .split("_")
            .map((part, index) => {
                if (index === 0) {
                    return part;
                }
                return part.charAt(0).toUpperCase() + part.slice(1);
            })
            .join("");
    };

    return template.replace(
        /\$([A-Z0-9_]+)(?:\s*\|([^$]*))?\$/gi,
        (_, rawKey, rawArgs) => {
            const key = toCamelCase(rawKey);
            let value = ctx[key];

            let arrayDelimiter = " - ";
            let defaultValue = "";

            if (rawArgs) {
                const argString = rawArgs.trim();

                const arrayDelimMatch = argString.match(
                    /ARRAY_DELIM:\s*([\s\S]*?)(?=\s+(?:DEFAULT:)|$)/i
                );
                if (arrayDelimMatch) {
                    arrayDelimiter = arrayDelimMatch[1].trim();
                }

                const defaultMatch = argString.match(
                    /DEFAULT:\s*([\s\S]*?)(?=\s+(?:ARRAY_DELIM:)|$)/i
                );
                if (defaultMatch) {
                    defaultValue = defaultMatch[1].trim();
                }
            }

            if (typeof value === "function") {
                value = value();
            }

            if (value == null || value === "") {
                return defaultValue;
            }

            if (Array.isArray(value)) {
                return value.join(arrayDelimiter);
            }

            return String(value);
        }
    );
};

const headerVariablesContext = {
    // ISO / technical formats
    dateIso8601: () => new Date().toISOString(),
    dateIsoDate: () => new Date().toISOString().slice(0, 10),
    dateIsoTime: () => new Date().toISOString().slice(11, 19),
    dateIsoUtc: () => new Date().toUTCString(),

    // Numeric components
    currentYear: () => String(new Date().getFullYear()),
    currentMonth: () => String(new Date().getMonth() + 1),
    day: () => String(new Date().getDate()),
    currentHour: () => String(new Date().getHours()),
    currentMinute: () => String(new Date().getMinutes()),
    currentSecond: () => String(new Date().getSeconds()),

    // Numeric combined formats
    dateYyyyMmDd: () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },

    dateDdMmYyyy: () => {
        const d = new Date();
        return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
    },

    dateMmDdYyyy: () => {
        const d = new Date();
        return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
    },

    // Textual / readable (browser locale only)
    dateShort: () => {
        const d = new Date();
        return d.toLocaleDateString(navigator.language);
    },

    dateLong: () => {
        const d = new Date();
        return d.toLocaleDateString(navigator.language, {
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    },

    dateFull: () => {
        const d = new Date();
        return d.toLocaleDateString(navigator.language, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    },

    dateWeekdayLong: () => {
        const d = new Date();
        return d.toLocaleDateString(navigator.language, { weekday: "long" });
    },

    dateWeekdayShort: () => {
        const d = new Date();
        return d.toLocaleDateString(navigator.language, { weekday: "short" });
    },

    // Time-only (browser locale)
    timeShort: () => {
        const d = new Date();
        return d.toLocaleTimeString(navigator.language);
    },

    timeLong: () => {
        const d = new Date();
        return d.toLocaleTimeString(navigator.language, {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
        });
    },

    timeHm: () => {
        const d = new Date();
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    },

    // Localized explicit (prefixed as requested)
    dateLocalizedShort: () => {
        const d = new Date();
        return d.toLocaleDateString(navigator.language);
    },

    dateLocalizedLong: () => {
        const d = new Date();
        return d.toLocaleDateString(navigator.language, {
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    },

    dateLocalizedFull: () => {
        const d = new Date();
        return d.toLocaleDateString(navigator.language, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    },

    dateLocalizedWeekday: () => {
        const d = new Date();
        return d.toLocaleDateString(navigator.language, { weekday: "long" });
    },

    timeLocalized: () => {
        const d = new Date();
        return d.toLocaleTimeString(navigator.language);
    },

    dateTimeLocalized: () => {
        const d = new Date();
        return d.toLocaleString(navigator.language);
    },

    // Combined readable
    dateTimeReadable: () => {
        const d = new Date();
        return `${d.toLocaleDateString(navigator.language)} ${d.toLocaleTimeString(navigator.language)}`;
    },

    isoCalendarWeek: () => {
        const d = new Date();
        const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        const day = date.getUTCDay() || 7; // ISO: Mon=1 … Sun=7
        date.setUTCDate(date.getUTCDate() + 4 - day);

        const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
        const week = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);

        return String(week).padStart(2, "0");
    },

    isoWeekYear: () => {
        const d = new Date();
        const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        const day = date.getUTCDay() || 7;
        date.setUTCDate(date.getUTCDate() + 4 - day);
        return String(date.getUTCFullYear());
    },

    // ISO-like local datetime (no timezone)
    dateTimeIsoLocal: () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
    }
};

const toScreamingSnakeCase = (value) =>
    value
        .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
        .replace(/([A-Z])([A-Z][a-z])/g, "$1_$2")
        .toUpperCase();

const headerVariables = Object.keys(headerVariablesContext).map(
    (key) => `$${toScreamingSnakeCase(key)}$`
);


const pageTitleTemplateYearOnly = { defaultTemplate: '$YEAR$', variables: ['$YEAR$'] };
const baseVisibleCustomisationOptions = ['pageType', 'orientation', 'years', 'themeSelector', 'leadingZero', 'startOnSundays', 'showWeekColumn', 'markedDates'];
const defaultVisibleCustomisationOptions = [...baseVisibleCustomisationOptions, 'pageTitle'/*, 'documentPageTitle'*/];

const getPageLayout = (layout) => pageLayouts[layout];

const getPageLayoutFactory = (layout, orientation, paperSize) => getPageLayout(layout).factories[orientation.toLowerCase()][paperSize];

const miniCalendarThemes = ["default", "defaultGreen", "defaultBlue", "noGridYellow", "noGridGreen", "noGridBlue", "noWeekendHighlight", "noWeekendHighlightNoGrid", "modern", "modern2", "modern3", "modern4", "modern5"];

const pageLayouts = {
    yearPerPage: {
        displayName: 'One year per page',
        pageTitleTemplateConfig: pageTitleTemplateYearOnly,
        visibleCustomisationOptions: defaultVisibleCustomisationOptions,
        supportedThemes: miniCalendarThemes,
        factories: {
            'portrait': {
                'A5': (c) => <YearPerPageA5 pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} />,
                'A4': (c) => <YearPerPageA4 pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} />,
                'Letter': (c) => <YearPerPageUSLetter pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} />,
            },
            'landscape': {
                'A5': (c) => <YearPerPageA5 pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} />,
                'A4': (c) => <YearPerPageA4 pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} />,
                'Letter': (c) => <YearPerPageUSLetter pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} />,
            }
        }
    },
    flexible: {
        displayName: 'Flexibe',
        pageTitleTemplateConfig: pageTitleTemplateYearOnly,
        visibleCustomisationOptions: [...baseVisibleCustomisationOptions, 'pageMargins', 'paddings', 'documentHeader', 'documentTitle'/*, 'documentPageTitle'*/, 'documentSectionTitle'],
        supportedThemes: miniCalendarThemes,
        factories: {
            'portrait': {
                //'A5': (c) => <YearPerPageA5 pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} />,
                'A4': (c) => <YearMonthMultiPageA4 templateParser={titleTemplateParser} sectionTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} document={c.document} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} {...c.layoutConfig?.flexible ?? {}} />,
                //'Letter': (c) => <YearPerPageUSLetter pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} />,
            },
            'landscape': {
                //'A5': (c) => <YearPerPageA5 pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} />,
                'A4': (c) => <YearMonthMultiPageA4 templateParser={titleTemplateParser} sectionTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} {...c.layoutConfig?.flexible ?? {}} />,
                //'Letter': (c) => <YearPerPageUSLetter pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} />,
            }
        }
    },
    monthPerPage: {
        displayName: 'One month per page',
        pageTitleTemplateConfig: { defaultTemplate: '$YEAR$ $MONTH_NAME$', variables: ['$YEAR$', '$MONTH$', '$MONTH_NAME$'] },
        visibleCustomisationOptions: [...defaultVisibleCustomisationOptions, 'alignmentContentCellHorizontal'],
        supportedThemes: miniCalendarThemes,
        factories: {
            'landscape': {
                'A4': (c) => <MonthPerPageA4 pageTitleTemplateParser={(year, month, monthName) => pageTitleTemplateParser(c.pageTitleTemplate, year, month, monthName)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={c.orientation} customMarkings={c.customMarkings} weekDays={getDayNames(navigator.language || navigator.userLanguage, 'long')} alignmentContentCellHorizontal={c.alignmentContentCellHorizontal} alignmentContentCellVertical={c.alignmentContentCellVertical} alignmentHeaderCell={c.alignmentHeaderCell} alignmentWeekCell={c.alignmentWeekCell} />,
            }
        }
    },
    sixMonthsPerPage: {
        displayName: 'Portrait six months per page',
        pageTitleTemplateConfig: pageTitleTemplateYearOnly,
        visibleCustomisationOptions: defaultVisibleCustomisationOptions,
        supportedThemes: miniCalendarThemes,
        factories: {
            'portrait': {
                'A4': (c) => <SixMonthsPerPageA4 pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={'portrait'} customMarkings={c.customMarkings} />,
            },
        }
    },
    twoColumnSixMonthsPerPageMonthsHorizontal: {
        displayName: 'Two column six months per page (months flow horizontal)',
        pageTitleTemplateConfig: pageTitleTemplateYearOnly,
        visibleCustomisationOptions: defaultVisibleCustomisationOptions,
        supportedThemes: miniCalendarThemes,
        factories: {
            'landscape': {
                'A4': (c) => <SixMonthsPerPageA4 pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={'landscape'} monthsVertical={false} customMarkings={c.customMarkings} />,
            }
        }
    },
    twoColumnSixMonthsPerPageMonthsVertical: {
        displayName: 'Two column six months per page (months flow vertical)',
        pageTitleTemplateConfig: pageTitleTemplateYearOnly,
        visibleCustomisationOptions: defaultVisibleCustomisationOptions,
        supportedThemes: miniCalendarThemes,
        factories: {
            'landscape': {
                'A4': (c) => <SixMonthsPerPageA4 pageTitleTemplateParser={(year) => pageTitleTemplateParser(c.pageTitleTemplate, year)} years={c.years} prependZero={c.prependZero} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} themeName={c.themeNameOrTheme} orientation={'landscape'} monthsVertical={true} customMarkings={c.customMarkings} />,
            }
        }
    },
    weekPerPage: {
        displayName: 'Week Overview',
        pageTitleTemplateConfig: { defaultTemplate: '$YEAR$ Week $WEEK$', variables: ['$YEAR$', '$WEEK$'] },
        visibleCustomisationOptions: ['pageType', 'orientation', 'weeks', 'pageTitle', 'showWeekends', 'startOnSundays', 'startOfDay'],
        supportedThemes: [],
        factories: {
            'portrait': {
                'A5': (c) => <WeekPerPageA5 pageTitleTemplateParser={(year, week) => pageTitleTemplateParser(c.pageTitleTemplate, year, undefined, undefined, week)} weeks={c.weeks} orientation={c.orientation} prependZero={c.prependZero} showWeekends={c.showWeekends} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} startOfDay={c.startOfDay} endOfDay={c.endOfDay} />,
                'A4': (c) => <WeekPerPageA4 pageTitleTemplateParser={(year, week) => pageTitleTemplateParser(c.pageTitleTemplate, year, undefined, undefined, week)} weeks={c.weeks} orientation={c.orientation} prependZero={c.prependZero} showWeekends={c.showWeekends} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} startOfDay={c.startOfDay} endOfDay={c.endOfDay} />,
            },
            'landscape': {
                'A5': (c) => <WeekPerPageA5 pageTitleTemplateParser={(year, week) => pageTitleTemplateParser(c.pageTitleTemplate, year, undefined, undefined, week)} weeks={c.weeks} orientation={c.orientation} prependZero={c.prependZero} showWeekends={c.showWeekends} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} startOfDay={c.startOfDay} endOfDay={c.endOfDay} />,
                'A4': (c) => <WeekPerPageA4 pageTitleTemplateParser={(year, week) => pageTitleTemplateParser(c.pageTitleTemplate, year, undefined, undefined, week)} weeks={c.weeks} orientation={c.orientation} prependZero={c.prependZero} showWeekends={c.showWeekends} startWeekOnSundays={c.startWeekOnSundays} showWeekColumn={c.showWeekColumn} startOfDay={c.startOfDay} endOfDay={c.endOfDay} />,
            }
        }
    }
};

export { getPageLayout, getPageLayoutFactory, pageLayouts, YearPerPageA4, YearPerPageA5, headerVariablesContext, headerVariables };